import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
} from "@headlessui/react";
import {
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";

// ProjectMenu component to display dropdown project list
const ProjectMenu = ({ menu }) => (
  <Popover className="relative text-brand-green">
    <PopoverButton className="flex items-center gap-x-1 text-sm font-semibold leading-6">
      {menu.name}
      <ChevronDownIcon
        aria-hidden="true"
        className="h-5 w-5 flex-none text-brand-green"
      />
    </PopoverButton>
    <PopoverPanel className="absolute -left-8 top-full z-20 mt-3 w-screen max-w-3xl 2xl:max-w-4xl overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-gray-900/5">
      <div className="bg-white py-10">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-6 lg:px-8 xl:grid-cols-3">
          <div className="mx-auto max-w-2xl lg:mx-0">
            <h2 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
              My Projects
            </h2>
            <p className="mt-6 text-md leading-8 text-gray-600">
              These are some of the top projects showcasing our skills in various technologies.
            </p>
            <a
              href="/projects"
              className="mt-4 inline-block text-sm text-indigo-500 hover:text-brand-light font-semibold leading-7"
            >
              More projects, click here →
            </a>
          </div>
          <ul role="list" className="mx-auto grid max-w-2xl grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:gap-x-8 xl:col-span-2">
            {menu.children.map((child) => (
              <li key={child.name} className="relative">
                <a href={child.href} className="block">
                  <div className="relative">
                    <img
                      alt={child.name}
                      src={child.imageUrl}
                      loading="lazy"
                      className="aspect-[3/2] w-full  rounded-2xl object-fill  myAnimation"
                    />
                    <img
                      src={child.logoUrl}
                      alt={`${child.name} logo`}
                      className="absolute top-2 right-2 h-8 w-8 rounded-lg bg-white p-1 shadow-md "
                    />
                  </div>
                  <h3 className="mt-2 text-base font-semibold leading-8 text-gray-900">
                    {child.name}
                  </h3>
                  <p className="leading-6 text-gray-600 text-xs">
                    {child.description}
                  </p>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </PopoverPanel>
  </Popover>
);

// MobileMenu component for mobile navigation
const MobileMenu = ({ menus, mobileMenuOpen, setMobileMenuOpen }) => (
  <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
    <div className="fixed inset-0 z-10" />
    <DialogPanel className="fixed inset-y-0 right-0 z-20 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-brand-green">
      <div className="flex items-center justify-between">
        <a href="#" className="-m-1.5 p-1.5">
          <img alt="Logo" src="/logo.png" className="h-12 w-auto rounded-xl shadow-2xl" />
        </a>
        <button
          type="button"
          onClick={() => setMobileMenuOpen(false)}
          className="-m-2.5 rounded-md p-2.5 text-brand-green"
        >
          <XMarkIcon aria-hidden="true" className="h-12 w-12" />
        </button>
      </div>
      <div className="mt-6 flow-root">
        <div className="-my-6 divide-y divide-brand-green">
          <div className="space-y-2 py-6">
            {menus.map((menu) => (
              <div className="transition-transform transform hover:translate-y-1 text-brand-green" key={menu.name}>
                {menu.children ? (
                  <Disclosure as="div" className="-mx-3">
                    <DisclosureButton className="group flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 hover:bg-gray-50">
                      {menu.name}
                      <ChevronDownIcon
                        aria-hidden="true"
                        className="h-5 w-5 flex-none group-data-[open]:rotate-180"
                      />
                    </DisclosureButton>
                    <DisclosurePanel className="mt-2 space-y-2 text-gray-900">
                      {menu.children.map((child) => (
                        <a
                          key={child.name}
                          href={child.href}
                          className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold leading-7 hover:bg-gray-50"
                        >
                          {child.name}
                          <div className="relative">
                            <img
                              alt={child.name}
                              src={child.imageUrl}
                              loading="lazy"
                              className="aspect-[3/2] w-full rounded-2xl object-contain py-1 myAnimation"
                            />
                            <img
                              src={child.logoUrl}
                              alt={`${child.name} logo`}
                              className="absolute top-2 right-2 h-8 w-8 rounded-lg bg-white p-1 shadow-md"
                            />
                          </div>
                          <p className="leading-7 text-gray-600 text-xs">
                            {child.description}
                          </p>
                        </a>
                      ))}
                      <a
                        href="/projects"
                        className="mt-4 pl-3 inline-block text-sm text-indigo-500 hover:text-brand-light font-semibold leading-7"
                      >
                        More projects, click here →
                      </a>
                    </DisclosurePanel>
                  </Disclosure>
                ) : (
                  <a
                    href={menu.href}
                    className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-brand-green hover:bg-gray-50"
                  >
                    {menu.name}
                  </a>
                )}
              </div>
            ))}
          </div>
          <div className="py-6">
            <a
              href="/login"
              className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-brand-green hover:bg-gray-50"
            >
              Login
            </a>
          </div>
        </div>
      </div>
    </DialogPanel>
  </Dialog>
);

// Header component for desktop and mobile navigation
export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const menus = useMemo(
    () => [
      { name: "Home", href: "/" },
      {
        name: "Projects",
        href: "#",
        children: [
          {
            name: "JavaScript Project",
            description:
              "A dynamic JavaScript project that showcases modern web development practices and utilizes advanced ES6+ features.",
            imageUrl: "/JS/jsEcom-home.png",
            logoUrl: "/JavaScript-logo.png",
            href: "/projects",
          },
          {
            name: "WordPress Project",
            description:
              "A fully customized WordPress theme designed for an eCommerce website, integrating modern plugins and custom features.",
            imageUrl: "/WordPress/ruby/rubynails1.png",
            logoUrl: "/Wordpress-Logo.svg",
            href: "/projects",
          },
          {
            name: "React.js Project",
            description:
              "A powerful single-page application built with React.js, showcasing advanced component architecture and state management.",
            imageUrl: "/adminDashboard/login.png",
            logoUrl: "/React-icon.svg",
            href: "/projects",
          },
          {
            name: "TypeScript Project",
            description:
              "A scalable project built with TypeScript that demonstrates the power of static typing in JavaScript for improved maintainability.",
            imageUrl: "/JS/snake-1.png",
                          
            logoUrl: "/Typescript_logo_2020.svg",
            href: "/projects",
          },
        ],
      },
      { name: "About", href: "/about" },
      { name: "Contact", href: "/contact" },
      { name: "Blog", href: "/blog" },
    ],
    []
  );

  return (
    <header className="bg-white sticky top-0 z-20 shadow-md">
      <nav
        aria-label="Global"
        className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8"
      >
        <div className="flex lg:flex-1">
          <a
            href="/"
            className="-m-1.5 p-1.5"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <img
              src={isHovered ? "/hover-logo.png" : "/logo.png"}
              alt="Logo"
              className="h-12 rounded-xl w-auto"
            />
          </a>
        </div>
        <div className="flex lg:hidden transition-transform transform hover:translate-y-1">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon aria-hidden="true" className="h-12 text-brand-green" />
          </button>
        </div>
        <PopoverGroup className="hidden lg:flex lg:gap-x-12">
          {menus.map((menu) =>
            menu.children ? (
              <ProjectMenu key={menu.name} menu={menu} />
            ) : (
              <a
                key={menu.name}
                href={menu.href}
                className="text-sm font-semibold leading-6 text-brand-green"
              >
                {menu.name}
              </a>
            )
          )}
        </PopoverGroup>
        <div className="hidden lg:flex lg:flex-1 lg:justify-end">
          <a
            href="/login"
            className="text-sm font-semibold leading-6 text-brand-green"
          >
            Login
          </a>
          <UsersIcon className="h-5 w-5 ml-2 text-brand-green" />
        </div>
      </nav>
      <MobileMenu
        menus={menus}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
    </header>
  );
}
